import { Grid, Paper, CircularProgress, Box, Button, Divider } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useTranslation } from 'react-i18next';
import { AddCircleRounded, Edit } from '@mui/icons-material';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { getEventById, updateEventAllergy } from 'src/shared/services/eventService';
import { useEventContext } from 'src/shared/contexts/eventContext';
import { getEventType } from 'src/shared/functions/getEventType';
import { useGlobals } from 'src/shared/contexts/appContext';
import { EventTypeEnum } from 'src/shared/models/enums/eventType.enum';
import { EventEditModal } from '../components/eventEditModal';
import { EventInviteParticipantsModal } from '../components/eventInviteParticipantsModal';
import EventDetailTabs, { EventTabItem, tabOptions } from '../components/eventDetailTabs';
import { EventMeEventType } from '../components/eventMeEventType';
import { EventDetailTabTickets } from '../components/eventDetailTabTickets';
import { EventDetailTabParticipants } from '../components/eventDetailTabParticipants';
import { EventDetailTabInformation } from '../components/eventDetailTabInformation';
import { EventDetailTabFormSettings } from '../components/eventDetailTabFormSettings';

export const EventDetailsPage = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { dateStamp, setDateStamp } = useGlobals();
  const { event, setEvent, editMode, setEditMode, setSelectedEventType, setUploadedImageFile, setUploadedImageUrl } = useEventContext();

  const [loading, setIsLoading] = useState(false);

  const [selectedTab, setSelectedTab] = useState<EventTabItem>(tabOptions[0]);
  const [createParticipants, setCreateParticipants] = useState<boolean>();

  const [inviteParticipantsModal, setInviteParticipantsModal] = useState(false);

  // Räkna ut hur många deltagare som är anmäld till eventet
  const eventType = event && getEventType(event?.publicSignUp, event?.membersOnly);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await getEventById(id);
      if (isMounted()) {
        setEvent(res.data);
        ConsoleHelper.log('EVENT DETAILS', res.data, dateStamp);
      }
    } catch (error) {
      if (isMounted()) {
        ConsoleHelper.log(error);
      }
    } finally {
      if (isMounted()) {
        setIsLoading(false);
      }
    }
  }, [id, isMounted, setEvent, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const onCloseHandler = () => {
    setEditMode(false);
    setEvent(undefined);
    setSelectedEventType(undefined);
    setUploadedImageFile(undefined);
    setUploadedImageUrl('');
    fetchData();
  };
  const onSaveHandler = () => {
    setEditMode(false);
    setEvent(undefined);
    setSelectedEventType(undefined);
    setUploadedImageFile(undefined);
    setUploadedImageUrl('');
    fetchData();
  };

  return (
    <>
      <GridWrapper>
        <Grid item xs sx={{ display: 'flex', flexDirection: 'column' }}>
          <MainContentHeader isLoading={loading} title={event?.name ?? ''} />
          {eventType && <EventMeEventType eventType={eventType} />}
        </Grid>
        {!loading && (
          <Grid item xs='auto' sx={{ display: 'flex', justifyContent: 'flex-end', gridColumnGap: '5px', placeItems: 'flex-start' }}>
            {!loading && event && eventType === EventTypeEnum.NonPublic ? (
              <Button
                size='medium'
                variant='contained'
                color='secondary'
                disableElevation
                onClick={() => {
                  setCreateParticipants(true);
                  setInviteParticipantsModal(true);
                }}
                startIcon={<AddCircleRounded />}
                sx={{}}
              >
                {t('event:inviteParticipants')}
              </Button>
            ) : (
              !event?.useTickets && ( // only show if useTickets is false
                <Button
                  size='medium'
                  variant='contained'
                  color='secondary'
                  disableElevation
                  onClick={() => {
                    setCreateParticipants(false);
                    setInviteParticipantsModal(true);
                  }}
                  startIcon={<AddCircleRounded />}
                >
                  {t('event:sendInvite')}
                </Button>
              )
            )}
            {event && (
              <Button size='medium' variant='outlined' onClick={() => setEditMode(true)} endIcon={<Edit />} sx={{}}>
                {t('common:button.edit')}
              </Button>
            )}
          </Grid>
        )}
        <Grid item xs={12}>
          <Paper>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                <CircularProgress role='alert' aria-busy='true' aria-label={t('common:statusMessages.loading')} />
              </Box>
            )}
            {!loading && event && (
              <>
                <Grid item xs={12}>
                  <EventDetailTabs
                    useTickets={event.useTickets}
                    initialValue={selectedTab}
                    callback={(item: EventTabItem) => {
                      setSelectedTab(item);
                    }}
                  />
                  <Divider />
                </Grid>
                <Grid item xs={12} p={3}>
                  {selectedTab.value === 1 && <EventDetailTabInformation event={event} />}
                  {selectedTab.value === 2 && <EventDetailTabTickets tickets={event.tickets} eventId={Number(id)} />}
                  {selectedTab.value === 3 && (
                    <EventDetailTabParticipants
                      participants={event.participants}
                      eventId={event.id}
                      eventFee={event.fee}
                      onAction={() => fetchData()}
                      useTickets={event.useTickets}
                    />
                  )}
                  {selectedTab.value === 4 && (
                    <EventDetailTabFormSettings
                      eventId={event.id}
                      hasParticipants={(event.participants?.length ?? 0) > 0}
                      showAllergy={event.allergy ?? false}
                      onToggleAllergy={async (enabled) => {
                        try {
                          await updateEventAllergy(event.id, enabled);
                          fetchData();
                        } catch (e) {
                          ConsoleHelper.log(e);
                        }
                      }}
                    />
                  )}
                </Grid>
              </>
            )}
          </Paper>
        </Grid>
      </GridWrapper>
      {event && editMode && (
        <EventEditModal
          event={event}
          onClose={onCloseHandler}
          onSave={onSaveHandler}
          callback={() => {
            ConsoleHelper.log('callback på edit event gör ingenting');
          }}
          modalOpen={editMode}
        />
      )}
      {event && inviteParticipantsModal && createParticipants !== undefined && (
        <EventInviteParticipantsModal
          eventId={event.id}
          createParticipants={createParticipants}
          modalOpen={inviteParticipantsModal}
          onClose={() => {
            setInviteParticipantsModal(false);
            setCreateParticipants(undefined);
          }}
          onSave={() => {
            setDateStamp(new Date());
            setInviteParticipantsModal(false);
            setCreateParticipants(undefined);
          }}
          callback={() => {
            ConsoleHelper.log('callback');
          }}
        />
      )}
    </>
  );
};
